/**
 * @author       justin.jeong@zumper.com (Justin Jeong)
 * @copyright    Copyright (c) 2022, Zumper
 */

import type { Unit } from '@zcode/bizos'
import type { IntegratedTourType } from '@zcode/enums'
import { type Origin, localStorageSingleton } from '@zcode/nr-utils'
import { TOUR } from '@zcode/nr/es/shared/constants/message'
import type { FloorplanListing, Listable, ListingData, Property } from '@zcode/state-type'
import { format } from 'date-fns'
import type { Dispatch } from 'redux'
import { checkDateValidity } from '../../../packages/Inputs/validator/moveIn'
import { getFPMatchingPreferences } from '../../MessageButton/getOneTapMessageData'
import { oneTapDataConflictsWithLoggedInUser } from '../../MessageButton/oneTapDataConflictsWithLoggedInUser'
import { getCustomMessagesByIntent } from '../../message/MessageForm/helpers/getCustomMessagesByIntent'

export interface GetOneTapTourDataProps {
  bedroomsArray: readonly boolean[]
  entity: Listable | Property | null | undefined
  hasInstantTourAvailability: boolean
  origin: Origin
  selectedFloorplan: FloorplanListing | undefined
  selectedUnit: Unit | undefined
}

export const getOneTapTourData = (tourData: GetOneTapTourDataProps) => (dispatch: Dispatch) => {
  const {
    entity,
    hasInstantTourAvailability,
    origin,
    bedroomsArray,
    selectedFloorplan,
    selectedUnit,
  } = tourData
  //  the following will only exist once the user sends a lead
  const savedOneTapFormInputs: {
    email?: string
    floorplan?: ListingData
    moveIn: string | null
    name?: string
    phone?: string | null
    requestedTimes?: string[]
    selectedTimes?: string[]
    tourType?: IntegratedTourType
  } = localStorageSingleton.get('one_tap_form_inputs')

  if (
    !entity ||
    entity.isCustomMessageRequired ||
    hasInstantTourAvailability ||
    !savedOneTapFormInputs ||
    !checkDateValidity(savedOneTapFormInputs.moveIn) ||
    // check for required phone
    (!savedOneTapFormInputs.phone && (entity.isPhoneRequired || entity.isListing)) ||
    !savedOneTapFormInputs.requestedTimes ||
    !savedOneTapFormInputs.selectedTimes ||
    savedOneTapFormInputs.requestedTimes
      .map((dateString) => checkDateValidity(dateString))
      .some((value) => !value) ||
    dispatch(oneTapDataConflictsWithLoggedInUser(savedOneTapFormInputs))
  ) {
    // we have no valid entity, no saved inputs, or the movein is no longer valid, or we found invalid tour times
    return null
  }

  const {
    email,
    floorplan: savedFP,
    moveIn,
    name,
    phone,
    requestedTimes,
    selectedTimes,
    tourType,
  } = savedOneTapFormInputs

  const fpMatchingPreferenences =
    selectedFloorplan ?? getFPMatchingPreferences(entity, bedroomsArray, savedFP)

  if (entity.isBuilding) {
    // we're messaging a building, but no specified floor plan is given
    if (!fpMatchingPreferenences) {
      // we weren't able to find a floor plan matching our previous choice
      return null
    }
  }
  const { headerText } = entity

  return {
    buildingId: entity.buildingId,
    listingId: entity.listingId,
    equalCTAIntent: TOUR as typeof TOUR,
    entity,
    oneTap: true as const,
    origin,
    requestedTimes,
    selectedTimes,
    showTourSuccessButterBar: true as const,
    formValues: {
      email,
      floorplanId: fpMatchingPreferenences?.id,
      message: getCustomMessagesByIntent({
        intent: TOUR,
        listingName: headerText,
      }),
      // move in for leads is formatted 'MM/dd/yyyy' but 'yyyy/MM/dd' in tour
      moveInDate: format(new Date(moveIn), 'yyyy/MM/dd'),
      name,
      phone,
      tourType,
      unitId: selectedUnit?.unitId,
    },
  }
}
